import { Heart } from 'lucide-react'
import { MetricRing } from '@/components/hearth'
import { cn } from '@/lib/utils'

interface WellbeingHeroProps {
  /** First name of the person this summary is about */
  name: string
  /** True when the viewer is looking at their own data */
  isSelf?: boolean
  /** Wellbeing score 0–100 when the backend provides one */
  score?: number | null
  /** First insight line from the health summary, if any */
  insight?: string
  className?: string
}

function heroCopy(score: number, name: string, isSelf: boolean) {
  if (score >= 70) {
    return isSelf ? "You're doing well" : `${name} is doing well`
  }
  if (score >= 40) {
    return isSelf ? "You're doing okay" : `${name} is doing okay`
  }
  return isSelf ? 'Keep an eye on your numbers' : `Keep an eye on ${name}`
}

function scoreColor(score: number) {
  if (score >= 70) return 'text-success-500'
  if (score >= 40) return 'text-warning-500'
  return 'text-error-500'
}

/**
 * Wellbeing hero (Hearth): a floating card with a wellbeing ring beside a
 * serif reassurance headline. Falls back to a calm "gathering" state while
 * we're still getting to know someone's rhythm.
 */
export function WellbeingHero({ name, isSelf = false, score, insight, className }: WellbeingHeroProps) {
  const hasScore = typeof score === 'number' && Number.isFinite(score)

  if (!hasScore) {
    return (
      <div
        className={cn(
          'hearth-card-floating flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left',
          className
        )}
      >
        <div className="flex h-[104px] w-[104px] shrink-0 items-center justify-center rounded-full border-2 border-dashed border-gray-300">
          <Heart className="h-7 w-7 text-gray-400" />
        </div>
        <div>
          <h2 className="font-serif text-xl font-semibold text-gray-900">
            Getting to know {isSelf ? 'your' : `${name}'s`} rhythm
          </h2>
          <p className="mt-1.5 max-w-md text-[15px] text-gray-500">
            A wellbeing summary appears after a few days of readings.
          </p>
        </div>
      </div>
    )
  }

  const rounded = Math.round(Math.min(Math.max(score, 0), 100))

  return (
    <div
      className={cn(
        'hearth-card-floating flex flex-col items-center gap-5 p-6 text-center sm:flex-row sm:text-left',
        className
      )}
    >
      <MetricRing
        progress={rounded / 100}
        value={String(rounded)}
        label="Wellbeing"
        colorClass={scoreColor(rounded)}
        size={104}
        className="shrink-0"
      />
      <div>
        <h2 className="font-serif text-xl font-semibold text-gray-900">
          {heroCopy(rounded, name, isSelf)}
        </h2>
        <p className="mt-1.5 max-w-md text-[15px] text-gray-500">
          {insight ?? `Based on ${isSelf ? 'your' : `${name}'s`} recent readings.`}
        </p>
      </div>
    </div>
  )
}
